import { portfolio, type CardSize, type PortfolioItem, type PortfolioVideo } from '../data/portfolio'
import { caseMeta } from '../data/workSeo'
import { casePath, homePath, parseRoute } from '../core/routes'
import type { Scroller } from '../core/scroll'
import { store } from '../core/store'
import { dictionaries, translate, type Lang } from '../i18n'
import { coverSvg } from './covers'
import { $, esc, icon, mediaUrl, pad } from './dom'

type WorkCopy = (typeof dictionaries)['en']['work']

// Desktop rhythm when an item has no explicit size: 7/5, then a full-width row, then 5/7…
const SIZE_PATTERN: CardSize[] = ['l', 's', 'xl', 's', 'l', 'm', 'm']

/** Cards shown before "Load more", and added by each click. */
export const WORK_PAGE = 3
let visible = WORK_PAGE

function coverMarkup(item: PortfolioItem, uid: string, opts: { eager?: boolean; wide?: boolean; alt?: string } = {}): string {
  if (item.cover) {
    return `<img src="${esc(mediaUrl(item.cover))}" alt="${esc(opts.alt ?? '')}" ${opts.eager ? '' : 'loading="lazy"'} decoding="async">`
  }
  return coverSvg(item.coverKind ?? 'site', uid, opts.wide)
}

function renderMore(grid: HTMLElement, lang: Lang, all: boolean): void {
  const host = grid.parentElement?.querySelector<HTMLElement>('[data-work-more]')
  if (!host) return
  const total = portfolio.length
  const shown = all ? total : Math.min(visible, total)
  host.hidden = shown >= total
  host.innerHTML = host.hidden
    ? ''
    : `<button type="button" class="btn btn--ghost" data-work-more-btn>${esc(dictionaries[lang].work.loadMore)}<span class="work-more__count mono">${shown} / ${total}</span></button>`
}

/**
 * Renders the card grid. In the browser only the first page is visible and the rest are `hidden` until
 * "Load more"; the prerender passes `all` so crawlers and no-JS visitors get every card.
 */
export function renderWork(grid: HTMLElement, lang: Lang, opts: { all?: boolean } = {}): void {
  const d = dictionaries[lang].work
  const shown = opts.all ? portfolio.length : visible
  grid.innerHTML = portfolio
    .map((item, i) => {
      const size = item.size ?? SIZE_PATTERN[i % SIZE_PATTERN.length]
      const title = item.title[lang]
      const href = casePath(lang, item.id)
      const meta = [item.category[lang], item.year].filter(Boolean).join(' · ')
      const preview = item.previewVideo
        ? `<video class="work-card__preview" src="${esc(mediaUrl(item.previewVideo))}" muted loop playsinline preload="none"></video>`
        : ''
      const online = item.links?.some((link) => link.kind === 'live')
      const soon =
        item.status === 'concept' && !item.links?.length ? `<p class="work-card__soon mono"><span aria-hidden="true"></span>${esc(d.soon)}</p>` : ''
      return `<article class="work-card" data-size="${size}" data-index="${i}" data-reveal style="--d:${(i % 2) * 120}ms"${i >= shown ? ' hidden' : ''}>
        <a class="work-card__media" href="${esc(href)}" data-case="${esc(item.id)}" aria-label="${esc(d.status[item.status])} ${esc(d.viewCase)}: ${esc(title)}">
          ${coverMarkup(item, `card-${item.id}`, { wide: size === 'xl' })}${preview}
          <span class="work-card__badge mono" data-status="${item.status}"${online ? ' data-online' : ''}>${esc(d.status[item.status])}</span>
          <span class="work-card__cta">${esc(d.viewCase)}${icon('i-arrow-up-right')}</span>
        </a>
        <div class="work-card__body">
        <div class="work-card__head">
          <h3 class="work-card__title"><span class="work-card__idx mono">${pad(i + 1)}</span><a class="work-card__link" href="${esc(href)}" data-case="${esc(item.id)}"><span data-scr>${esc(title)}</span></a></h3>
          <p class="work-card__meta mono">${esc(meta)}</p>
        </div>
        <p class="work-card__summary">${esc((item.summary ?? item.description)[lang])}</p>
        ${soon}
        <ul class="tags">${item.tags[lang].map((t) => `<li class="tag">${esc(t)}</li>`).join('')}</ul>
        </div>
      </article>`
    })
    .join('')

  renderMore(grid, lang, !!opts.all)

  // Hover previews: play muted loops only while hovered.
  grid.querySelectorAll<HTMLVideoElement>('.work-card__preview').forEach((video) => {
    const host = video.parentElement!
    host.addEventListener('pointerenter', () => void video.play().catch(() => {}))
    host.addEventListener('pointerleave', () => video.pause())
  })
}

/** Reveals the next page of cards (or enough of them to show card `upTo - 1`). Returns the first card shown. */
export function showMoreWork(grid: HTMLElement, upTo = visible + WORK_PAGE): HTMLElement | null {
  const total = portfolio.length
  visible = Math.min(total, Math.max(visible, Math.ceil(upTo / WORK_PAGE) * WORK_PAGE))
  let first: HTMLElement | null = null
  grid.querySelectorAll<HTMLElement>('.work-card[hidden]').forEach((card) => {
    if (Number(card.dataset.index) >= visible) return
    card.hidden = false
    void card.offsetWidth // let the reveal transition start from the hidden state
    card.classList.add('is-in')
    first ??= card
  })
  renderMore(grid, store.get().lang, false)
  return first
}

function videoId(src: string, kind: 'youtube' | 'vimeo'): string {
  if (kind === 'youtube') {
    const match = src.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)
    return match ? match[1] : src
  }
  const match = src.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  return match ? match[1] : src
}

function playerMarkup(video: PortfolioVideo): string {
  if (video.kind === 'file') {
    const poster = video.poster ? ` poster="${esc(mediaUrl(video.poster))}"` : ''
    return `<video src="${esc(mediaUrl(video.src))}"${poster} controls autoplay playsinline></video>`
  }
  const src =
    video.kind === 'youtube'
      ? `https://www.youtube-nocookie.com/embed/${videoId(video.src, 'youtube')}?autoplay=1&rel=0&modestbranding=1`
      : `https://player.vimeo.com/video/${videoId(video.src, 'vimeo')}?autoplay=1&dnt=1`
  return `<iframe src="${esc(src)}" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`
}

export function caseCrumb(index: number, lang: Lang): string {
  return `${pad(index + 1)} / ${pad(portfolio.length)} · ${dictionaries[lang].work.status[portfolio[index].status]}`
}

/** Inner markup of the case sheet. Shared with the build-time prerender, so /work/<id>/ ships complete HTML. */
export function caseMarkup(index: number, lang: Lang): string {
  const item = portfolio[index]
  const d: WorkCopy = dictionaries[lang].work
  const next = portfolio[(index + 1) % portfolio.length]
  const title = item.title[lang]

  const links = (item.links ?? [])
    .map(
      (link) =>
        `<a class="btn btn--ghost btn--sm" href="${esc(link.url)}" target="_blank" rel="noopener noreferrer">${esc(d.links[link.kind])}${icon('i-arrow-up-right', 'btn__trail')}</a>`,
    )
    .join('')
  const promo = item.video
    ? `<button class="btn btn--primary btn--sm" type="button" data-play>${icon('i-play', 'btn__lead')}${esc(d.promo)}</button>`
    : ''

  const facts = [
    item.client && [d.client, esc(item.client)],
    item.year && [d.year, esc(item.year)],
    [d.category, esc(item.category[lang])],
    [d.scope, `<ul class="tags">${item.tags[lang].map((t) => `<li class="tag">${esc(t)}</li>`).join('')}</ul>`],
    item.stack?.length && [d.stack, `<ul class="tags">${item.stack.map((t) => `<li class="tag">${esc(t)}</li>`).join('')}</ul>`],
  ]
    .filter((row): row is [string, string] => Array.isArray(row))
    .map(([label, value]) => `<div><dt class="mono">${esc(label)}</dt><dd>${value}</dd></div>`)
    .join('')

  const highlightList = item.highlights?.[lang]
  const highlights = highlightList?.length
    ? `<section class="case__highlights">
        <p class="mono case__label">${esc(d.highlights)}</p>
        <ul>${highlightList.map((line) => `<li>${esc(line)}</li>`).join('')}</ul>
      </section>`
    : ''

  const gallery = item.gallery?.length
    ? item.gallery
        .map(
          (shot, i) => `<figure class="shot" data-shot-index="${i}">
            <button type="button" data-shot="${i}" aria-label="${esc(shot.caption?.[lang] ?? `${d.screens} ${i + 1}`)}">
              <img src="${esc(mediaUrl(shot.src))}" alt="${esc(shot.caption ? `${title}: ${shot.caption[lang]}` : '')}" loading="lazy" decoding="async">
            </button>
            ${shot.caption ? `<figcaption class="mono">${esc(shot.caption[lang])}</figcaption>` : ''}
          </figure>`,
        )
        .join('')
    : [1, 2, 3].map((n) => `<div class="shot shot--slot"><span class="mono">${pad(n)}</span></div>`).join('') +
      `<p class="case__soon">${esc(d.screensSoon)}</p>`

  const previewLine =
    item.gallery && item.gallery.length > 1
      ? `<div class="gallery-preview-line" data-lenis-prevent>
          <button class="gallery-preview-line__nav" type="button" data-gallery-prev aria-label="${esc(d.prevScreen ?? 'Previous screen')}" disabled>
            ${icon('i-chev-left')}
          </button>
          <div class="gallery-thumbs" role="tablist" aria-label="${esc(d.screens)}">
            ${item.gallery
              .map(
                (shot, i) => `<button type="button" class="gallery-thumb${i === 0 ? ' is-active' : ''}" data-thumb="${i}" role="tab" aria-selected="${i === 0 ? 'true' : 'false'}" aria-label="${esc(shot.caption?.[lang] ?? `${d.screens} ${i + 1}`)}" title="${esc(shot.caption?.[lang] ?? `${d.screens} ${i + 1}`)}">
                  <img src="${esc(mediaUrl(shot.src))}" alt="" loading="lazy" decoding="async">
                  <span class="gallery-thumb__idx mono">${pad(i + 1)}</span>
                </button>`,
              )
              .join('')}
          </div>
          <button class="gallery-preview-line__nav" type="button" data-gallery-next aria-label="${esc(d.nextScreen ?? 'Next screen')}">
            ${icon('i-chev-right')}
          </button>
        </div>`
      : ''

  const lightboxThumbs =
    item.gallery && item.gallery.length > 1
      ? `<div class="lightbox__preview-line" data-lenis-prevent>
          <div class="lightbox__thumbs" role="tablist" aria-label="${esc(d.screens)}">
            ${item.gallery
              .map(
                (shot, i) => `<button type="button" class="lightbox__thumb${i === 0 ? ' is-active' : ''}" data-lightbox-thumb="${i}" role="tab" aria-selected="${i === 0 ? 'true' : 'false'}" aria-label="${esc(shot.caption?.[lang] ?? `${d.screens} ${i + 1}`)}" title="${esc(shot.caption?.[lang] ?? `${d.screens} ${i + 1}`)}">
                  <img src="${esc(mediaUrl(shot.src))}" alt="" loading="lazy" decoding="async">
                  <span class="lightbox__thumb-idx mono">${pad(i + 1)}</span>
                </button>`,
              )
              .join('')}
          </div>
        </div>`
      : ''

  return `
    <header class="case__head">
      <p class="kicker mono"><span class="idx">${pad(index + 1)}</span><span>${esc(item.category[lang])}</span></p>
      <h1 class="case__title" id="case-title">${esc(title)}</h1>
      <div class="case__intro">
        <p class="case__lead">${esc((item.summary ?? item.description)[lang])}</p>
        ${links || promo ? `<div class="case__links">${promo}${links}</div>` : ''}
      </div>
    </header>
    <div class="case__media">
      ${coverMarkup(item, `case-${item.id}`, { eager: true, alt: title })}
      ${item.video ? `<button class="case__play" type="button" data-play aria-label="${esc(d.promo)}">${icon('i-play')}</button>` : ''}
    </div>
    <div class="case__grid">
      <div class="case__desc">
        <p>${esc(item.description[lang])}</p>
        ${item.outcome ? `<p class="mono case__label">${esc(d.outcome)}</p><p class="case__outcome">${esc(item.outcome[lang])}</p>` : ''}
      </div>
      <dl class="case__facts">${facts}</dl>
    </div>
    ${highlights}
    <section class="case__gallery">
      <div class="case__gallery-head">
        <p class="mono case__label">${esc(d.screens)}</p>
        ${item.gallery?.length ? `<span class="case__gallery-count mono" data-gallery-count>01 / ${pad(item.gallery.length)}</span>` : ''}
      </div>
      <div class="gallery-strip" data-lenis-prevent>${gallery}</div>
      ${previewLine}
    </section>
    ${
      portfolio.length > 1
        ? `<a class="case__next" href="${esc(casePath(lang, next.id))}" data-case-next>
            <span class="mono">${esc(d.next)}</span>
            <span class="case__next-title">${esc(next.title[lang])}</span>
            ${icon('i-arrow')}
          </a>`
        : ''
    }
    <div class="lightbox" hidden>
      <button class="icon-btn" type="button" data-lightbox-close aria-label="${esc(d.close)}">${icon('i-close')}</button>
      <div class="lightbox__stage">
        ${item.gallery && item.gallery.length > 1 ? `<button class="lightbox__nav lightbox__nav--prev" type="button" data-lightbox-prev aria-label="${esc(d.prevScreen ?? 'Previous screen')}">${icon('i-chev-left')}</button>` : ''}
        <figure class="lightbox__figure">
          <img alt="" src="" loading="lazy">
          <figcaption class="mono"></figcaption>
        </figure>
        ${item.gallery && item.gallery.length > 1 ? `<button class="lightbox__nav lightbox__nav--next" type="button" data-lightbox-next aria-label="${esc(d.nextScreen ?? 'Next screen')}">${icon('i-chev-right')}</button>` : ''}
      </div>
      ${lightboxThumbs}
    </div>`
}

type HistoryMode = 'push' | 'replace' | 'none'

/**
 * Full-screen case-study sheet built on <dialog> (focus trap + Esc for free).
 * Every case has a real URL (/work/<id>/ and the /me/, /ru/ variants): opening pushes it, Back closes the
 * sheet, and a direct visit to the prerendered page opens the same sheet over the home page.
 */
export class CaseViewer {
  private dialog = $<HTMLDialogElement>('#case')
  private body = $('#case-body')
  private crumb = $('#case-crumb')
  private index = -1
  private returnFocus: HTMLElement | null = null
  /** True while the open sheet owns a history entry we pushed (Back, not replaceState, closes it). */
  private pushed = false
  /** Set when the browser already moved through history (popstate), so closing must not touch it. */
  private skipHistoryOnClose = false
  private activeShotIndex = 0
  private lightboxIndex = -1
  private stripObserver: IntersectionObserver | null = null

  constructor(
    private readonly scroller: Scroller,
    /** Reveals the grid card for `index` (it may sit behind "Load more"). */
    private readonly ensureVisible: (index: number) => void = () => {},
  ) {
    this.dialog.addEventListener('click', (event) => this.onClick(event))
    this.dialog.addEventListener('keydown', (event) => {
      const lightbox = this.body.querySelector<HTMLElement>('.lightbox:not([hidden])')
      if (lightbox) {
        if (event.key === 'ArrowRight') {
          event.preventDefault()
          this.stepLightbox(1)
          return
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          this.stepLightbox(-1)
          return
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          this.closeLightbox()
          return
        }
      }
      if (document.activeElement?.closest('.gallery-preview-line')) {
        if (event.key === 'ArrowRight') {
          event.preventDefault()
          this.stepGallery(1)
          return
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          this.stepGallery(-1)
          return
        }
      }
      if (event.key === 'ArrowRight') this.step(1)
      if (event.key === 'ArrowLeft') this.step(-1)
    })
    this.dialog.addEventListener('cancel', (event) => {
      const lightbox = this.body.querySelector<HTMLElement>('.lightbox:not([hidden])')
      if (lightbox) {
        event.preventDefault()
        this.closeLightbox()
      }
    })
    this.dialog.addEventListener('close', () => this.onClose())
    window.addEventListener('popstate', () => this.syncFromLocation('none'))
  }

  get isOpen(): boolean {
    return this.dialog.open
  }

  /** Opens the case named by the current URL (direct visit or Back/Forward), or closes the sheet if there is none. */
  syncFromLocation(mode: HistoryMode = 'replace'): void {
    const { caseId } = parseRoute(location.pathname)
    if (caseId && portfolio.some((item) => item.id === caseId)) {
      this.open(caseId, undefined, mode)
    } else if (this.dialog.open) {
      this.skipHistoryOnClose = true
      this.close()
    }
  }

  open(id: string, trigger?: HTMLElement, mode: HistoryMode = 'push'): void {
    const index = portfolio.findIndex((item) => item.id === id)
    if (index < 0) return
    this.index = index
    this.returnFocus = trigger ?? null
    this.ensureVisible(index)
    this.render()
    this.syncUrl(mode)
    // The prerendered page ships the sheet as a plain `open` dialog; promote it to a modal one.
    if (this.dialog.open && !this.dialog.matches(':modal')) this.dialog.removeAttribute('open')
    if (!this.dialog.open) {
      this.scroller.lock()
      this.dialog.showModal()
    }
    this.dialog.scrollTop = 0
  }

  close(): void {
    if (this.dialog.open) this.dialog.close()
  }

  /** Re-render in place, e.g. after a language switch. */
  refresh(): void {
    if (this.dialog.open && this.index >= 0) {
      this.render()
      this.syncUrl('replace')
    }
  }

  private onClose(): void {
    this.stripObserver?.disconnect()
    this.stripObserver = null
    this.activeShotIndex = 0
    this.lightboxIndex = -1
    this.body.innerHTML = '' // stops any playing video
    this.index = -1
    this.scroller.unlock()
    this.returnFocus?.focus({ preventScroll: true })
    document.title = translate(store.get().lang, 'meta.title')
    document.querySelector('meta[name="description"]')?.setAttribute('content', translate(store.get().lang, 'meta.description'))

    const skip = this.skipHistoryOnClose
    this.skipHistoryOnClose = false
    if (skip) {
      this.pushed = false
    } else if (this.pushed) {
      this.pushed = false
      history.back()
    } else if (parseRoute(location.pathname).caseId) {
      // Landed on the case URL directly: there is no earlier entry to go back to, so show the home page.
      history.replaceState(null, '', `${homePath(store.get().lang)}#work`)
      const work = document.getElementById('work')
      if (work) this.scroller.to(work)
    }
  }

  private syncUrl(mode: HistoryMode): void {
    if (mode === 'none' || this.index < 0) return
    const target = casePath(store.get().lang, portfolio[this.index].id)
    if (location.pathname === target) return
    if (mode === 'push') {
      history.pushState(null, '', target)
      this.pushed = true
    } else {
      history.replaceState(null, '', target)
    }
  }

  private step(direction: number): void {
    if (this.index < 0 || portfolio.length < 2) return
    this.index = (this.index + direction + portfolio.length) % portfolio.length
    this.ensureVisible(this.index)
    this.render()
    this.syncUrl('replace')
    this.dialog.scrollTo({ top: 0, behavior: 'smooth' })
  }

  private onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement
    if (target.closest('[data-case-close]')) return this.close()
    if (target.closest('[data-case-prev]')) return this.step(-1)
    if (target.closest('[data-case-next]')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      return this.step(1)
    }
    if (target.closest('[data-play]')) return this.play()

    // Gallery preview line
    const thumb = target.closest<HTMLElement>('[data-thumb]')
    if (thumb) {
      event.preventDefault()
      return this.setActiveShot(Number(thumb.dataset.thumb), true)
    }
    if (target.closest('[data-gallery-prev]')) {
      event.preventDefault()
      return this.stepGallery(-1)
    }
    if (target.closest('[data-gallery-next]')) {
      event.preventDefault()
      return this.stepGallery(1)
    }

    // Main shot click -> open lightbox
    const shot = target.closest<HTMLElement>('[data-shot]')
    if (shot) return this.openLightbox(Number(shot.dataset.shot))

    // Lightbox interactions
    if (target.closest('[data-lightbox-prev]')) {
      event.preventDefault()
      return this.stepLightbox(-1)
    }
    if (target.closest('[data-lightbox-next]')) {
      event.preventDefault()
      return this.stepLightbox(1)
    }
    const lbThumb = target.closest<HTMLElement>('[data-lightbox-thumb]')
    if (lbThumb) {
      event.preventDefault()
      return this.setLightbox(Number(lbThumb.dataset.lightboxThumb))
    }
    if (
      target.closest('[data-lightbox-close]') ||
      target.classList.contains('lightbox') ||
      target.classList.contains('lightbox__stage')
    ) {
      this.closeLightbox()
    }
  }

  private play(): void {
    const item = portfolio[this.index]
    const media = this.body.querySelector('.case__media')
    if (item?.video && media) media.innerHTML = playerMarkup(item.video)
  }

  private initGallerySync(): void {
    const strip = this.body.querySelector<HTMLElement>('.gallery-strip')
    const shots = this.body.querySelectorAll<HTMLElement>('.shot[data-shot-index]')
    if (!strip || !shots.length) return

    if ('IntersectionObserver' in window) {
      this.stripObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const idx = Number((entry.target as HTMLElement).dataset.shotIndex)
              if (!Number.isNaN(idx)) {
                this.setActiveShot(idx, false)
              }
            }
          }
        },
        { root: strip, threshold: 0.6 },
      )
      shots.forEach((shot) => this.stripObserver?.observe(shot))
    }
  }

  private setActiveShot(idx: number, scrollStrip = true): void {
    const item = portfolio[this.index]
    const total = item?.gallery?.length ?? 0
    if (total <= 0) return
    this.activeShotIndex = Math.max(0, Math.min(idx, total - 1))

    const thumbs = this.body.querySelectorAll<HTMLButtonElement>('.gallery-thumb')
    thumbs.forEach((thumb, i) => {
      const isActive = i === this.activeShotIndex
      thumb.classList.toggle('is-active', isActive)
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false')
      if (isActive) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
      }
    })

    const counter = this.body.querySelector<HTMLElement>('[data-gallery-count]')
    if (counter) counter.textContent = `${pad(this.activeShotIndex + 1)} / ${pad(total)}`

    const prevBtn = this.body.querySelector<HTMLButtonElement>('[data-gallery-prev]')
    const nextBtn = this.body.querySelector<HTMLButtonElement>('[data-gallery-next]')
    if (prevBtn) prevBtn.disabled = this.activeShotIndex <= 0
    if (nextBtn) nextBtn.disabled = this.activeShotIndex >= total - 1

    if (scrollStrip) {
      const shot = this.body.querySelector<HTMLElement>(`.shot[data-shot-index="${this.activeShotIndex}"]`)
      if (shot) {
        shot.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
      }
    }
  }

  private stepGallery(delta: number): void {
    const total = portfolio[this.index]?.gallery?.length ?? 0
    if (total <= 1) return
    const nextIdx = Math.max(0, Math.min(total - 1, this.activeShotIndex + delta))
    this.setActiveShot(nextIdx, true)
  }

  private openLightbox(i: number): void {
    const item = portfolio[this.index]
    const total = item?.gallery?.length ?? 0
    if (!item?.gallery || i < 0 || i >= total) return
    const box = this.body.querySelector<HTMLElement>('.lightbox')
    if (!box) return
    this.lightboxIndex = i
    this.updateLightboxContent()
    box.hidden = false
    box.querySelector<HTMLElement>('[data-lightbox-close]')?.focus()
  }

  private setLightbox(i: number): void {
    const total = portfolio[this.index]?.gallery?.length ?? 0
    if (total <= 0) return
    this.lightboxIndex = (i + total) % total
    this.updateLightboxContent()
  }

  private stepLightbox(delta: number): void {
    const total = portfolio[this.index]?.gallery?.length ?? 0
    if (total <= 1) return
    this.lightboxIndex = (this.lightboxIndex + delta + total) % total
    this.updateLightboxContent()
  }

  private updateLightboxContent(): void {
    const item = portfolio[this.index]
    const shot = item?.gallery?.[this.lightboxIndex]
    const box = this.body.querySelector<HTMLElement>('.lightbox')
    if (!shot || !box) return
    const lang = store.get().lang
    const img = box.querySelector<HTMLImageElement>('figure img')
    const caption = box.querySelector<HTMLElement>('figcaption')
    if (img) img.src = mediaUrl(shot.src)
    if (caption) caption.textContent = shot.caption?.[lang] ?? ''

    const thumbs = box.querySelectorAll<HTMLButtonElement>('.lightbox__thumb')
    thumbs.forEach((thumb, idx) => {
      const isActive = idx === this.lightboxIndex
      thumb.classList.toggle('is-active', isActive)
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false')
      if (isActive) {
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
      }
    })
  }

  private closeLightbox(): void {
    const box = this.body.querySelector<HTMLElement>('.lightbox')
    if (box) box.hidden = true
    if (this.lightboxIndex >= 0) {
      this.setActiveShot(this.lightboxIndex, true)
    }
    this.lightboxIndex = -1
  }

  private render(): void {
    this.stripObserver?.disconnect()
    this.stripObserver = null
    this.activeShotIndex = 0
    this.lightboxIndex = -1
    const lang = store.get().lang
    this.crumb.textContent = caseCrumb(this.index, lang)
    this.body.innerHTML = caseMarkup(this.index, lang)
    document.title = caseMeta(portfolio[this.index], lang).title
    this.initGallerySync()
  }
}
